import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { usePartnerConfigHelper } from '@white-label-helper/shared-helpers';
import { fetchFeefoReviews } from '@white-label-helper/init-feefo-reviews';
import { useCartStore, CartStoreState } from '@white-label-store/cart';
import { getFeatureFlag } from '@white-label-plugin/launch-darkly';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { datadogRum } from '@datadog/browser-rum';

import type {
  ParkingFormData,
  LoungeFormData,
} from '@white-label-types/search-box';
import type { SearchProducts } from '@white-label-types/parking-booking';
import {
  PARKING_CATEGORY_TYPES,
  PARKING_SORT_OPTIONS,
  PRODUCT_TYPES,
  ProductType,
  FEATURE_FLAGS
} from '@white-label-configuration/constants';

type Playbook = { order: number; product_id: number };
type Playbooks = {
  featured: Playbook[];
  boosted: Playbook[];
  bottom: Playbook[];
};
type SearchResultsFormData = ParkingFormData | LoungeFormData | null;
type Timestamp = Date | string | null;
type FilterCategory = typeof PARKING_CATEGORY_TYPES[keyof typeof PARKING_CATEGORY_TYPES];
type InventoryMetadata = { [key: string]: boolean };
type BookingFee = {
  is_enabled: boolean;
  amount: number;
}
type SearchResultsByProductType = { [key in typeof PRODUCT_TYPES[keyof typeof PRODUCT_TYPES]]?: SearchProducts[] };
type MapSearch = {
  showMap: boolean;
  highlightedMarker: number | string | null;
  resetMap: boolean;
}

export const useSearchResultsStore = defineStore('searchResults', () => {
  const searchResults = ref<SearchProducts[]>([]);
  const searchResultsByProductType = ref<SearchResultsByProductType>({
    [PRODUCT_TYPES.PARKING]: [],
    [PRODUCT_TYPES.LOUNGES]: [],
    [PRODUCT_TYPES.FASTTRACK]: [],
    [PRODUCT_TYPES.TRAVEL_INSURANCE]: [],
    [PRODUCT_TYPES.EXTRAS]: [],
  });
  const searchExtrasResults = ref<SearchProducts[]>([]);
  const searchResultsFormData = ref<SearchResultsFormData>({
    entryDate: '',
    entryTime: null,
    exitDate: '',
    exitTime: null,
  });
  const searchResultsTimestamp = ref<Timestamp>(null);
  const searchErrorApi = ref(false);
  const searchToken = ref('')
  const playbooks = ref<Playbooks>({
    featured: [],
    boosted: [],
    bottom: [],
  });
  const yourSearchProductId = ref<number | null>(null);
  const utmSource = ref<string | undefined>('');
  const utmMedium = ref<string | undefined>('');
  const activeFilterCategoryType = ref<FilterCategory>(PARKING_CATEGORY_TYPES.ALL);
  const activeSort = ref('recomended');
  const inventoryMetadata = ref<InventoryMetadata>({});
  const mapSearch = ref<MapSearch>({
    showMap: true,
    highlightedMarker: null,
    resetMap: false,
  });
  const bookingFee = ref<BookingFee | null>(null);

  const dateTimeOfLastSearch = ref<Date | null>(null)

  function updateDateTimeOfLastSearch(date: Date | null): void {
    dateTimeOfLastSearch.value = date;
  }

  function currentSearchHasExpired (): boolean {
    if (!dateTimeOfLastSearch.value) return false;
    const FIFTEEN_MINUTES = 15 * 60 * 1000;
    const now = Date.now();
    return (now - dateTimeOfLastSearch.value.getTime() > FIFTEEN_MINUTES);
  }

  function sortProducts(products: SearchProducts[]) {
    const filteredSearchResults = activeFilterCategoryType.value === PARKING_CATEGORY_TYPES.ALL
        ? [...products]
        : products.filter(
            (result) =>
              result.product_option.code === activeFilterCategoryType.value
          );

    if (activeSort.value === PARKING_SORT_OPTIONS.PRICE) {
      return filteredSearchResults.sort((first, second) => {
        const firstPrice =
          first.productOptions[0].totals.channel_based.total_discounted;
        const firstName = first.name;
        const secondPrice =
          second.productOptions[0].totals.channel_based.total_discounted;
        const secondName = second.name;

        if (firstPrice > secondPrice) {
          return 1;
        }
        if (firstPrice < secondPrice) {
          return -1;
        }
        if (firstPrice === secondPrice) {
          if (firstName > secondName) {
            return 1;
          }
          if (firstName < secondName) {
            return -1;
          }
        }
        return 0;
      });
    }

    if (activeSort.value === PARKING_SORT_OPTIONS.DISTANCE) {
      return filteredSearchResults.sort((first, second) => {
        const firstDistance = Number(first.transfer.distance);
        const firstName = first.name;
        const secondDistance = Number(second.transfer.distance);
        const secondName = second.name;

        if (firstDistance > secondDistance) {
          return 1;
        }
        if (firstDistance < secondDistance) {
          return -1;
        }
        if (firstDistance === secondDistance) {
          if (firstName > secondName) {
            return 1;
          }
          if (firstName < secondName) {
            return -1;
          }
        }
        return 0;
      });
    }

    const yourSearchProduct = filteredSearchResults.find(
      (item) => item.id === yourSearchProductId.value
    );

    const featured = filteredSearchResults
      .filter((item) =>
        playbooks.value?.featured.some(
          (product) =>
            product.product_id === item.id &&
            product.product_id !== yourSearchProduct?.id
        )
      )
      .sort((first, second) => {
        const firstOptions = playbooks.value?.featured.find(
          (product) => product.product_id === first.id
        );
        const secondOptions = playbooks.value?.featured.find(
          (product) => product.product_id === second.id
        );

        if (firstOptions && secondOptions) {
          return firstOptions.order > secondOptions.order ? 1 : -1;
        }
        return 0;
      });

    const boosted = filteredSearchResults
      .filter((item) =>
        playbooks.value?.boosted.some(
          (product) =>
            product.product_id === item.id &&
            product.product_id !== yourSearchProduct?.id
        )
      )
      .sort((first, second) => {
        const firstOptions = playbooks.value?.boosted.find(
          (product) => product.product_id === first.id
        );
        const secondOptions = playbooks.value?.boosted.find(
          (product) => product.product_id === second.id
        );

        if (firstOptions && secondOptions) {
          return firstOptions.order > secondOptions.order ? 1 : -1;
        }
        return 0;
      });

    const bottom = filteredSearchResults
      .filter((item) =>
        playbooks.value?.bottom.some(
          (product) =>
            product.product_id === item.id &&
            product.product_id !== yourSearchProduct?.id
        )
      )
      .sort((first, second) => {
        const firstOptions = playbooks.value?.bottom.find(
          (product) => product.product_id === first.id
        );
        const secondOptions = playbooks.value?.bottom.find(
          (product) => product.product_id === second.id
        );

        if (firstOptions && secondOptions) {
          return firstOptions.order > secondOptions.order ? 1 : -1;
        }
        return 0;
      });

    const others = filteredSearchResults
      .filter(
        (item) =>
          !featured.some((product) => product.id === item.id) &&
          !boosted.some((product) => product.id === item.id) &&
          !bottom.some((product) => product.id === item.id) &&
          yourSearchProduct?.id !== item.id
      )
      .sort((first, second) => {
        const firstPrice =
          first.productOptions[0].totals.channel_based.total_discounted;
        const firstName = first.name;
        const secondPrice =
          second.productOptions[0].totals.channel_based.total_discounted;
        const secondName = second.name;

        if (firstPrice > secondPrice) {
          return 1;
        }
        if (firstPrice < secondPrice) {
          return -1;
        }
        if (firstPrice === secondPrice) {
          if (firstName > secondName) {
            return 1;
          }
          if (firstName < secondName) {
            return -1;
          }
        }
        return 0;
      });
    return [
      // If a product is available which matches the product id from query string, show this at the top of results
      ...(yourSearchProduct ? [yourSearchProduct] : []),
      // Otherwise, featured products show at the top of the results. Order 1 = First
      ...featured,
      // Boosted products will be listed after featured, and ordered based on BE
      ...boosted,
      ...others,
      // Bottom products will be listed last, and ordered based on BE
      ...bottom,
    ];
  }

   const sortedSearchResults = computed(() => {
    return sortProducts(searchResults.value);
  });

  const sortedSearchExtrasResults = computed(() => {
    return sortProducts(searchExtrasResults.value);
  });
  const { secondaryProductTypes } = usePartnerConfigHelper();

  const hasExtrasSearchResults = computed(() => {
    return secondaryProductTypes.some((productType: ProductType) => (
      (searchResultsByProductType.value[productType]?.length ?? 0) > 0
    ))
  });

  const hasExtrasSearchResultsByCategory = computed(() => {
    const { lounges = [], fasttrack = [] } = searchResultsByProductType.value;
    const inboundFasttrack = fasttrack.filter((item) => item.product_option.code === 'flying_in');
    const outboundFasttrack = fasttrack.filter((item) => item.product_option.code === 'flying_out');
    return {
      inboundFasttrack: inboundFasttrack.length > 0,
      outboundFasttrack: outboundFasttrack.length > 0,
      lounges: lounges.length > 0,
    }
  });

  const hasExtrasSearchResultsNotInBundle = computed(() => {
    const bundle: CartStoreState['bundle'] = useCartStore().readBundle;
    const enabledUnifiedExtrasFlow = getFeatureFlag(FEATURE_FLAGS.EM_1149_ENABLE_UNIFIED_EXTRAS_FLOW);
    const secondaryProducts = secondaryProductTypes
      .filter((productType) => {
        if (productType === PRODUCT_TYPES.FASTTRACK) {
          if (bundle?.hasInboundFasttrack && bundle?.hasOutboundFasttrack) return false;
          if (bundle?.hasInboundFasttrack && !hasExtrasSearchResultsByCategory.value.outboundFasttrack) return false;
          if (bundle?.hasOutboundFasttrack && !hasExtrasSearchResultsByCategory.value.inboundFasttrack) return false;
          return true;
        }
        return !(bundle?.productTypesInBundle ?? []).includes(productType);
      });
    if (!enabledUnifiedExtrasFlow) {
      const index = secondaryProducts.indexOf(PRODUCT_TYPES.EXTRAS);
      if (index !== -1) {
        secondaryProducts.splice(index, 1);
      }
    }

    return secondaryProducts.some((productType) => (searchResultsByProductType.value[productType]?.length ?? 0) > 0)
  });

  function updateSearchResults(
    payload: {
      searchResults: SearchProducts[];
      formData: SearchResultsFormData;
      timestamp: Timestamp;
      yourSearchProductId: number | null;
      utmSource: string | undefined;
      utmMedium: string | undefined;
    }
  ) {
    searchResults.value = payload.searchResults;
    // @ts-expect-error - At the moment the types are too inconsistent to get this work
    searchResultsFormData.value = { ...payload.formData };
    searchResultsTimestamp.value = payload.timestamp;
    yourSearchProductId.value = payload.yourSearchProductId;
    utmSource.value = payload.formData?.utmSource;
    utmMedium.value = payload.formData?.utmMedium;
  }

  function updateExtrasSearchResults(
    payload: {
      searchResults: SearchProducts[];
      formData: SearchResultsFormData;
      timestamp: Timestamp;
    }
  ) {
    searchExtrasResults.value = payload.searchResults;
    // @ts-expect-error - At the moment the types are too inconsistent to get this work
    searchResultsFormData.value = { ...payload.formData };
    searchResultsTimestamp.value = payload.timestamp;
  }

  function updateSearchResultsByProductType(
    payload: {
      searchResults: SearchProducts[];
      productType: keyof SearchResultsByProductType;
    }
  ) {
    searchResultsByProductType.value[payload.productType] = payload.searchResults;
  }

  function updateYourSearchProductId(payload: number) {
    yourSearchProductId.value = payload;
  }

  function clearSearchResults() {
    searchResults.value = [];
    searchResultsFormData.value = null;
    searchResultsTimestamp.value = null;
  }

  function clearSearchResultsByProductType() {
    searchResultsByProductType.value = {
      [PRODUCT_TYPES.PARKING]: [],
      [PRODUCT_TYPES.LOUNGES]: [],
      [PRODUCT_TYPES.FASTTRACK]: [],
      [PRODUCT_TYPES.TRAVEL_INSURANCE]: [],
      [PRODUCT_TYPES.EXTRAS]: [],
    };
  }

  function updateSearchToken(payload: string) {
    searchToken.value = payload;
  }

  function updatePlaybooks(payload: Playbooks) {
    playbooks.value = payload;
  }

  function updateActiveSort(payload: string) {
    activeSort.value = payload;
  }

  function updateSearchErrorApi(payload: boolean) {
    searchErrorApi.value = payload;
  }

  function updateActiveFilterCategoryType(payload: FilterCategory) {
    activeFilterCategoryType.value = payload;
  }

  function setCartId(payload: { id: number; cart_item_id: string; isTravelExtras: boolean }) {
    const results = payload.isTravelExtras ? searchExtrasResults.value : searchResults.value;
    const productIndex = results.findIndex(
      (item) => item.id === payload.id
    );
    if (productIndex >= 0) {
      results[productIndex].cart_item_id = payload.cart_item_id;
    }
  }

  function removeCartItemId(
    payload: { id?: number; cart_item_id: string, isTravelExtras?: boolean  }
  ) {
    if ( payload.isTravelExtras === undefined){
      payload.isTravelExtras = false;
    }
    const results = payload.isTravelExtras ? searchExtrasResults.value : searchResults.value;
    const productIndex = results.findIndex(
      (item) => item?.cart_item_id === payload.cart_item_id
    );
    if (productIndex >= 0) {
      results[productIndex].cart_item_id = '';
    }
  }

  function convertSearchResults(payload: {
    searchResults: SearchProducts[];
    formData: SearchResultsFormData;
    timestamp: Timestamp;
    yourSearchProductId: number | null;
  }) {
    return new Promise<void>((resolve) => {
      const { searchResults } = payload;

      updateSearchResults({
        searchResults: searchResults.length ? searchResults : [],
        formData: payload.formData,
        timestamp: payload.timestamp,
        yourSearchProductId: payload.yourSearchProductId,
        utmMedium: payload.formData?.utmMedium,
        utmSource: payload.formData?.utmSource,
      });

      if (getFeatureFlag('ecom-3559-fee-fo-api-integration', false)) {
        getFeefoReviews();
      }

      resolve();
    });
  }

  function convertExtrasSearchResults(
    payload: {
      searchResults: SearchProducts[];
      formData: SearchResultsFormData;
      timestamp: Timestamp;
    }
  ) {
    return new Promise<void>((resolve) => {
      const { searchResults } = payload;

      updateExtrasSearchResults({
        searchResults: searchResults.length ? searchResults : [],
        formData: payload.formData,
        timestamp: payload.timestamp,
      });
      resolve();
    });
  }

  function updateInventoryMetadata(payload: InventoryMetadata): void {
    inventoryMetadata.value = payload;
  }

  function updateBookingFee(payload: BookingFee) {
    bookingFee.value = payload;
  }

  function toggleMapSearch(payload: MapSearch['showMap']): void {
    mapSearch.value.showMap = payload;
  }

  function mapSearchReset(payload: MapSearch['resetMap']): void {
    mapSearch.value.resetMap = payload;
  }

  function updateMapHighlightedMarker(payload: MapSearch['highlightedMarker']): void {
    mapSearch.value.highlightedMarker = payload;
  }

  /**
   * Fetches Feefo reviews for each product in the search results
   *
   * @returns {Promise<void>}
   */
  async function getFeefoReviews(): Promise<void> {
    if (!getAppVariable('review_providers[0].is_enabled')) {
      return;
    }

    const productSkuArray = searchResults.value.map(item => item.productHeader.inventory_id);
    let errorCount = 0;
    let feeFoError: unknown = null;

    if (!productSkuArray.length) {
      return Promise.resolve();
    }

    // Due to the current structure in FeeFo we cannot make a single API call with all product SKUs as we cannot query legacy SKUs. We must use the parent SKU for now which brings back the full summary rating for each individual product.
    for (let index = 0; index < productSkuArray.length; index++) {
      const parentProductSku = productSkuArray[index];

      // We need to reduce error noise from failing FeeFo API calls, so we will retry up to 3 times before logging an error
      if (errorCount < 3) {
        try {
          const response = await fetchFeefoReviews(parentProductSku);
          const rating = response?.data?.rating;

          if (rating || rating > 0) {
            const store = useSearchResultsStore();
            store.$patch((state) => {
              const updatedResults = [...state.searchResults];
              updatedResults[index] = {
                ...updatedResults[index],
                reviewSummary: rating,
              };
              state.searchResults = updatedResults;
            });
          }
        } catch (error) {
          feeFoError = error;
          errorCount++;
        }
      } else {
        const context = {
          file: 'white-label-helper > init-feefo-reviews > getFeefoReviews()',
          productSku: parentProductSku,
        };

        datadogRum.addError(
          new Error('FeeFo fetchFeefoReviews:', { cause: feeFoError }),
          context
        );

        break;
      }
    }
  }

  return {
    searchResults,
    searchResultsByProductType,
    searchExtrasResults,
    searchResultsFormData,
    searchResultsTimestamp,
    searchErrorApi,
    searchToken,
    playbooks,
    yourSearchProductId,
    utmSource,
    utmMedium,
    activeFilterCategoryType,
    activeSort,
    sortedSearchResults,
    sortedSearchExtrasResults,
    hasExtrasSearchResults,
    hasExtrasSearchResultsByCategory,
    hasExtrasSearchResultsNotInBundle,
    clearSearchResults,
    clearSearchResultsByProductType,
    updateSearchToken,
    updatePlaybooks,
    currentSearchHasExpired,
    updateDateTimeOfLastSearch,
    updateActiveSort,
    updateSearchErrorApi,
    updateActiveFilterCategoryType,
    updateSearchResultsByProductType,
    updateYourSearchProductId,
    setCartId,
    removeCartItemId,
    convertSearchResults,
    convertExtrasSearchResults,
    inventoryMetadata,
    updateInventoryMetadata,
    bookingFee,
    updateBookingFee,
    mapSearch,
    toggleMapSearch,
    mapSearchReset,
    updateMapHighlightedMarker,
    getFeefoReviews,
  };
},
{
  // @ts-expect-error - At the moment the types are too inconsistent to get this work, upgrade to Vue 3 needed
  persist: {
    storage: process.client ? sessionStorage : undefined,
    paths: ['yourSearchProductId', 'utmSource', 'utmMedium']
  }
}
);
